import { api, setToken, UserInfo } from '../api';
import { setUser } from '../state';
import { navigate, refresh } from '../lib/router';
import { escapeHtml } from '../lib/utils';

interface AssociatedProject {
  id: string;
  name: string;
  description: string;
}

interface PreloginResult {
  email: string;
  hasAccount: boolean;
  projects: AssociatedProject[];
}

function authCard(inner: string): string {
  return `
    <div class="auth-wrap">
      <div class="auth-card">${inner}</div>
    </div>
  `;
}

export function renderAuth(app: HTMLElement, mode: 'login' | 'signup'): void {
  if (mode === 'signup') {
    renderSignup(app);
    return;
  }

  let prelogin: PreloginResult | null = null;

  const setError = (message: string) => {
    const el = app.querySelector('#auth-error') as HTMLElement | null;
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('hidden', !message);
  };

  const finishAuth = (token: string, user: UserInfo) => {
    setToken(token);
    setUser(user);
    navigate('/projects');
    refresh();
  };

  const showEmailStep = (email = '') => {
    app.innerHTML = authCard(`
      <h1>Welcome back</h1>
      <p class="auth-sub">Enter your email to sign in or view your projects.</p>
      <div class="form-error hidden" id="auth-error"></div>
      <form id="auth-form">
        <div class="field">
          <label for="email">Email</label>
          <input class="input" id="email" name="email" type="email" placeholder="you@example.com" autocomplete="email" value="${escapeHtml(email)}" />
        </div>
        <button class="btn btn-primary btn-block" type="submit" id="auth-submit">Continue</button>
      </form>
      <p style="margin-top:20px;font-size:14px;color:var(--text-2);text-align:center;">
        Don't have an account? <a href="#/signup">Sign up</a>
      </p>
    `);

    const form = app.querySelector('#auth-form') as HTMLFormElement;
    const submitBtn = app.querySelector('#auth-submit') as HTMLButtonElement;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      setError('');
      const data = new FormData(form);
      const email = String(data.get('email') || '').trim();
      if (!email) return;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Checking…';
      try {
        const res = await api.post<PreloginResult>('/auth/prelogin', { email });
        prelogin = res;
        showOptionsStep(res);
      } catch (err: any) {
        setError(err.message || 'Something went wrong');
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Continue';
      }
    });

    app.querySelector<HTMLInputElement>('#email')?.focus();
  };

  const showOptionsStep = (data: PreloginResult) => {
    prelogin = data;
    const projects = data.projects || [];
    app.innerHTML = authCard(`
      <h1>Choose how to continue</h1>
      <p class="auth-sub">
        You're signed in as <strong>${escapeHtml(data.email)}</strong>
        <a href="#" id="change-email" style="margin-left:8px;font-weight:600;">Change</a>
      </p>
      <div class="form-error hidden" id="auth-error"></div>
      ${
        projects.length > 0
          ? `
        <div class="auth-projects">
          <h2>View a project</h2>
          <p class="muted">You're associated with these projects. Open one in read-only view mode.</p>
          <div class="auth-project-list">
            ${projects
              .map(
                (p) => `
              <div class="auth-project-item">
                <div class="auth-project-info">
                  <strong>${escapeHtml(p.name)}</strong>
                  ${p.description ? `<span>${escapeHtml(p.description)}</span>` : ''}
                </div>
                <button class="btn btn-ghost btn-sm" data-view="${p.id}">View</button>
              </div>
            `
              )
              .join('')}
          </div>
        </div>
      `
          : `
        <div class="empty-state" style="margin-bottom:20px;">
          <p class="muted">No projects are associated with this email yet.</p>
        </div>
      `
      }
      <button class="btn btn-primary btn-block" id="continue-signin">Continue sign in</button>
      <p class="muted" style="margin-top:14px;font-size:13px;text-align:center;">
        ${
          data.hasAccount
            ? "You'll be asked to enter your password next."
            : "No account found for this email — you'll be able to create one."
        }
      </p>
    `);

    app.querySelector('#change-email')?.addEventListener('click', (e) => {
      e.preventDefault();
      showEmailStep(data.email);
    });

    app.querySelector('#continue-signin')?.addEventListener('click', () => {
      if (data.hasAccount) showPasswordStep(data.email);
      else showSignupStep(data.email);
    });

    app.querySelectorAll<HTMLButtonElement>('[data-view]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const projectId = btn.dataset.view!;
        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Opening…';
        try {
          const res = await api.post<{ token: string }>('/auth/view-token', {
            email: data.email,
            projectId,
          });
          navigate(`/share/${res.token}`);
          refresh();
        } catch (err: any) {
          setError(err.message || 'Could not open project');
          btn.disabled = false;
          btn.textContent = original;
        }
      });
    });
  };

  const showPasswordStep = (email: string) => {
    app.innerHTML = authCard(`
      <h1>Enter your password</h1>
      <p class="auth-sub">Continue signing in as <strong>${escapeHtml(email)}</strong></p>
      <div class="form-error hidden" id="auth-error"></div>
      <form id="auth-form">
        <div class="field">
          <label for="password">Password</label>
          <input class="input" id="password" name="password" type="password" placeholder="Your password" autocomplete="current-password" />
        </div>
        <button class="btn btn-primary btn-block" type="submit" id="auth-submit">Log in</button>
      </form>
      <button class="btn btn-ghost btn-block" id="back-btn" style="margin-top:12px;">Back</button>
    `);

    const form = app.querySelector('#auth-form') as HTMLFormElement;
    const submitBtn = app.querySelector('#auth-submit') as HTMLButtonElement;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      setError('');
      const data = new FormData(form);
      const password = String(data.get('password') || '');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Please wait…';
      try {
        const res = await api.post<{ token: string; user: UserInfo }>('/auth/login', {
          email,
          password,
        });
        finishAuth(res.token, res.user);
      } catch (err: any) {
        setError(err.message || 'Something went wrong');
        submitBtn.disabled = false;
        submitBtn.textContent = 'Log in';
      }
    });

    app.querySelector('#back-btn')?.addEventListener('click', () => {
      if (prelogin) showOptionsStep(prelogin);
      else showEmailStep(email);
    });
    app.querySelector<HTMLInputElement>('#password')?.focus();
  };

  const showSignupStep = (email: string) => {
    app.innerHTML = authCard(`
      <h1>Create your account</h1>
      <p class="auth-sub">No account exists for <strong>${escapeHtml(email)}</strong> yet.</p>
      <div class="form-error hidden" id="auth-error"></div>
      <form id="auth-form">
        <div class="field">
          <label for="name">Your name</label>
          <input class="input" id="name" name="name" placeholder="Ada Lovelace" autocomplete="name" />
        </div>
        <div class="field">
          <label for="email">Email</label>
          <input class="input" id="email" name="email" type="email" value="${escapeHtml(email)}" autocomplete="email" />
        </div>
        <div class="field">
          <label for="password">Password</label>
          <input class="input" id="password" name="password" type="password" placeholder="At least 6 characters" autocomplete="new-password" />
        </div>
        <button class="btn btn-primary btn-block" type="submit" id="auth-submit">Sign up free</button>
      </form>
      <button class="btn btn-ghost btn-block" id="back-btn" style="margin-top:12px;">Back</button>
    `);

    const form = app.querySelector('#auth-form') as HTMLFormElement;
    const submitBtn = app.querySelector('#auth-submit') as HTMLButtonElement;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      setError('');
      const data = new FormData(form);
      const payload = {
        name: String(data.get('name') || ''),
        email: String(data.get('email') || ''),
        password: String(data.get('password') || ''),
      };
      submitBtn.disabled = true;
      submitBtn.textContent = 'Please wait…';
      try {
        const res = await api.post<{ token: string; user: UserInfo }>('/auth/signup', payload);
        finishAuth(res.token, res.user);
      } catch (err: any) {
        setError(err.message || 'Something went wrong');
        submitBtn.disabled = false;
        submitBtn.textContent = 'Sign up free';
      }
    });

    app.querySelector('#back-btn')?.addEventListener('click', () => {
      if (prelogin) showOptionsStep(prelogin);
      else showEmailStep(email);
    });
    app.querySelector<HTMLInputElement>('#name')?.focus();
  };

  showEmailStep();
}

function renderSignup(app: HTMLElement): void {
  app.innerHTML = `
    <div class="auth-wrap">
      <div class="auth-card">
        <h1>Create your account</h1>
        <p class="auth-sub">Start tracking your projects with FlowBoard.</p>
        <div class="form-error hidden" id="auth-error"></div>
        <form id="auth-form">
          <div class="field">
            <label for="name">Your name</label>
            <input class="input" id="name" name="name" placeholder="Ada Lovelace" autocomplete="name" />
          </div>
          <div class="field">
            <label for="email">Email</label>
            <input class="input" id="email" name="email" type="email" placeholder="you@example.com" autocomplete="email" />
          </div>
          <div class="field">
            <label for="password">Password</label>
            <input class="input" id="password" name="password" type="password" placeholder="At least 6 characters" autocomplete="new-password" />
          </div>
          <button class="btn btn-primary btn-block" type="submit" id="auth-submit">Sign up free</button>
        </form>
        <p style="margin-top:20px;font-size:14px;color:var(--text-2);text-align:center;">
          Already have an account? <a href="#/login">Log in</a>
        </p>
      </div>
    </div>
  `;

  const form = app.querySelector('#auth-form') as HTMLFormElement;
  const errorEl = app.querySelector('#auth-error') as HTMLElement;
  const submitBtn = app.querySelector('#auth-submit') as HTMLButtonElement;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorEl.classList.add('hidden');
    const data = new FormData(form);
    const payload = {
      name: String(data.get('name') || ''),
      email: String(data.get('email') || ''),
      password: String(data.get('password') || ''),
    };
    submitBtn.disabled = true;
    submitBtn.textContent = 'Please wait…';
    try {
      const res = await api.post<{ token: string; user: UserInfo }>('/auth/signup', payload);
      setToken(res.token);
      setUser(res.user);
      navigate('/projects');
      refresh();
    } catch (err: any) {
      errorEl.textContent = err.message || 'Something went wrong';
      errorEl.classList.remove('hidden');
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Sign up free';
    }
  });

  app.querySelector<HTMLInputElement>('#name')?.focus();
}
